import { useCallback, useEffect, useState } from 'react';
import { companiesApi, hrApi } from '@/api';
import { PageActionGate } from '@/components/auth/PageActionGate';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column, TableRowActions } from '@/components/ui/DataTable';
import { FormGrid } from '@/components/ui/FormGrid';
import { FormActions } from '@/components/ui/FormActions';
import { Input } from '@/components/ui/Input';
import { TimePicker } from '@/components/ui/TimePicker';
import type { HrWeekPattern, HrWeekPatternDay, HrWeekPatternForm } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { cn } from '@/utils/cn';

const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function defaultDays(start = '09:00:00', end = '18:00:00'): HrWeekPatternDay[] {
  return DAY_LABELS.map((_, i) => ({
    dayOfWeek: i + 1,
    workingDay: i < 5,
    halfDay: false,
    startTime: i < 5 ? start : undefined,
    endTime: i < 5 ? end : undefined,
  }));
}

function normalizeTime(value: string): string {
  if (!value) return '09:00:00';
  return value.length === 5 ? `${value}:00` : value;
}

type Props = {
  companyId?: number;
  compact?: boolean;
  className?: string;
  selectedId?: number | null;
  onSelect?: (pattern: HrWeekPattern) => void;
  onPatternsChange?: (patterns: HrWeekPattern[]) => void;
  /** Keep compact inline buttons (e.g. inside modals). Default: centered FormActions row. */
  inlineActions?: boolean;
};

export function HrWeekPatternsSection({
  companyId,
  compact,
  className,
  selectedId,
  onSelect,
  onPatternsChange,
  inlineActions = false,
}: Props) {
  const [patterns, setPatterns] = useState<HrWeekPattern[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultStart, setDefaultStart] = useState('09:00');
  const [defaultEnd, setDefaultEnd] = useState('18:00');
  const [sortOrder, setSortOrder] = useState('0');
  const [days, setDays] = useState<HrWeekPatternDay[]>(defaultDays());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = companyId
        ? await companiesApi.listWeekPatterns(companyId)
        : await hrApi.listWeekPatterns(true);
      setPatterns(rows);
      onPatternsChange?.(rows.filter((p) => p.active));
    } catch (err) {
      showApiError(err, 'Failed to load week patterns');
    } finally {
      setLoading(false);
    }
  }, [companyId, onPatternsChange]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = () => {
    setEditId(null);
    setCode('');
    setName('');
    setDescription('');
    setDefaultStart('09:00');
    setDefaultEnd('18:00');
    setSortOrder('0');
    setDays(defaultDays());
  };

  const startEdit = (row: HrWeekPattern) => {
    setEditId(row.id);
    setCode(row.code);
    setName(row.name);
    setDescription(row.description ?? '');
    setDefaultStart(row.defaultStartTime?.slice(0, 5) ?? '09:00');
    setDefaultEnd(row.defaultEndTime?.slice(0, 5) ?? '18:00');
    setSortOrder(String(row.sortOrder ?? 0));
    setDays(row.days?.length === 7 ? row.days : defaultDays(row.defaultStartTime, row.defaultEndTime));
  };

  const applyDefaultsToWorkingDays = () => {
    const start = normalizeTime(defaultStart);
    const end = normalizeTime(defaultEnd);
    setDays((prev) => prev.map((d) => (d.workingDay
      ? { ...d, startTime: start, endTime: end }
      : { ...d, startTime: undefined, endTime: undefined })));
  };

  const buildBody = (): HrWeekPatternForm => ({
    code: code.trim().toUpperCase(),
    name: name.trim(),
    description: description.trim() || undefined,
    defaultStartTime: normalizeTime(defaultStart),
    defaultEndTime: normalizeTime(defaultEnd),
    sortOrder: Number(sortOrder) || 0,
    active: true,
    days: days.map((d) => ({
      dayOfWeek: d.dayOfWeek,
      workingDay: d.workingDay,
      halfDay: d.halfDay,
      startTime: d.workingDay ? normalizeTime(d.startTime ?? defaultStart) : undefined,
      endTime: d.workingDay ? normalizeTime(d.endTime ?? defaultEnd) : undefined,
    })),
  });

  const save = async () => {
    if (!code.trim() || !name.trim()) {
      notify.error('Code and name are required');
      return;
    }
    setSaving(true);
    try {
      const body = buildBody();
      if (editId != null) {
        if (companyId) await companiesApi.updateWeekPattern(companyId, editId, body);
        else await hrApi.updateWeekPattern(editId, body);
        notify.success('Week pattern updated');
      } else {
        if (companyId) await companiesApi.createWeekPattern(companyId, body);
        else await hrApi.createWeekPattern(body);
        notify.success('Week pattern created');
      }
      resetForm();
      await load();
    } catch (err) {
      showApiError(err, editId != null ? 'Update failed' : 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (id: number) => {
    try {
      if (companyId) await companiesApi.deactivateWeekPattern(companyId, id);
      else await hrApi.deactivateWeekPattern(id);
      notify.success('Week pattern deactivated');
      if (editId === id) resetForm();
      await load();
    } catch (err) {
      showApiError(err, 'Deactivate failed');
    }
  };

  const updateDay = (dayOfWeek: number, patch: Partial<HrWeekPatternDay>) => {
    setDays((prev) => prev.map((d) => (d.dayOfWeek === dayOfWeek ? { ...d, ...patch } : d)));
  };

  const columns: Column<HrWeekPattern>[] = [
    { key: 'code', header: 'Code' },
    {
      key: 'name',
      header: 'Name',
      render: (r) => (
        <span className={cn(selectedId === r.id && 'font-semibold text-primary-600 dark:text-primary-400')}>
          {r.name}
        </span>
      ),
    },
    {
      key: 'days',
      header: 'Working days',
      render: (r) => {
        const count = r.days?.filter((d) => d.workingDay).length ?? 0;
        return `${count} / 7`;
      },
    },
    {
      key: 'hours',
      header: 'Default hours',
      render: (r) => `${r.defaultStartTime?.slice(0, 5)} – ${r.defaultEndTime?.slice(0, 5)}`,
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <TableRowActions>
          {onSelect ? (
            <button
              type="button"
              className="text-sm text-primary-600 hover:underline dark:text-primary-400"
              onClick={() => onSelect(r)}
            >
              Use
            </button>
          ) : null}
          <PageActionGate action="edit">
            <button type="button" className="text-sm hover:underline" onClick={() => startEdit(r)}>
              Edit
            </button>
          </PageActionGate>
          <PageActionGate action="delete">
            <button type="button" className="text-sm text-red-600 hover:underline" onClick={() => void deactivate(r.id)}>
              Deactivate
            </button>
          </PageActionGate>
        </TableRowActions>
      ),
    },
  ];

  return (
    <div className={cn('space-y-4', className)}>
      {!compact && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Create company-specific week patterns. Assign one to the work calendar — no fixed presets required.
        </p>
      )}

      <DataTable
        columns={columns}
        data={patterns}
        loading={loading}
        keyField="id"
      />

      <PageActionGate action="create">
        <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
          <h4 className="mb-3 text-sm font-semibold">
            {editId != null ? 'Edit week pattern' : 'New week pattern'}
          </h4>
          <FormGrid columns={compact ? 2 : 3}>
            <Input label="Code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="MON_FRI_9_6" />
            <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Monday – Friday" />
            <Input label="Sort order" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
            <Input
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className={compact ? '' : 'sm:col-span-2'}
            />
            <TimePicker label="Default start" value={defaultStart} onChange={setDefaultStart} />
            <TimePicker label="Default end" value={defaultEnd} onChange={setDefaultEnd} />
          </FormGrid>

          <div className="mt-4 flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-slate-500">Days (1 = Monday)</p>
            <Button type="button" variant="secondary" size="sm" onClick={applyDefaultsToWorkingDays}>
              Apply default times to working days
            </Button>
          </div>

          <div className="mt-2 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b text-left text-slate-500">
                  <th className="py-2 pr-3">Day</th>
                  <th className="py-2 pr-3">Working</th>
                  <th className="py-2 pr-3">Half day</th>
                  <th className="py-2 pr-3">Start</th>
                  <th className="py-2">End</th>
                </tr>
              </thead>
              <tbody>
                {days.map((d) => (
                  <tr key={d.dayOfWeek} className="border-b border-slate-100 dark:border-slate-800">
                    <td className="py-2 pr-3 font-medium">{DAY_LABELS[d.dayOfWeek - 1]}</td>
                    <td className="py-2 pr-3">
                      <input
                        type="checkbox"
                        checked={d.workingDay}
                        onChange={(e) => updateDay(d.dayOfWeek, {
                          workingDay: e.target.checked,
                          halfDay: e.target.checked ? d.halfDay : false,
                        })}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        type="checkbox"
                        disabled={!d.workingDay}
                        checked={d.halfDay}
                        onChange={(e) => updateDay(d.dayOfWeek, { halfDay: e.target.checked })}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <TimePicker
                        value={d.startTime?.slice(0, 5) ?? defaultStart}
                        onChange={(v) => updateDay(d.dayOfWeek, { startTime: normalizeTime(v) })}
                        disabled={!d.workingDay}
                      />
                    </td>
                    <td className="py-2">
                      <TimePicker
                        value={d.endTime?.slice(0, 5) ?? defaultEnd}
                        onChange={(v) => updateDay(d.dayOfWeek, { endTime: normalizeTime(v) })}
                        disabled={!d.workingDay}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {inlineActions ? (
            <div className="mt-4 flex justify-end gap-2">
              {editId != null && (
                <Button type="button" variant="secondary" onClick={resetForm}>
                  Cancel
                </Button>
              )}
              <Button type="button" onClick={save} loading={saving}>
                {editId != null ? 'Update pattern' : 'Create pattern'}
              </Button>
            </div>
          ) : (
            <FormActions
              primaryLabel={editId != null ? 'Update pattern' : 'Create pattern'}
              primaryLoading={saving}
              onPrimary={save}
              onCancel={editId != null ? resetForm : undefined}
              onReset={editId == null ? resetForm : undefined}
            />
          )}
        </div>
      </PageActionGate>
    </div>
  );
}
